import React, {
    useCallback, useContext, useMemo, useState
} from 'react';
import CommonConstants from 'sg-common-constants';
import SmiConstants from 'sg-smi-constants';
import htmlParser from 'html-react-parser';
import { TranslatorContext } from '@jutro/locale';
import {
    Link,
    Button,
    Accordion,
    AccordionCard
} from '@jutro/components';
import { BreakpointTrackerContext } from '@jutro/layout';
import messages from './SgSmiError.messages';

function SgSmiError(props) {
    const {
        errorCodeT,
        errorCodeC
    } = props;

    const {
        SG_ERROR_CODES,
        SAGA_HOME
    } = CommonConstants;

    const {
        SG_SMI_ERROR_REASONS
    } = SmiConstants;

    const translator = useContext(TranslatorContext);
    const breakpoint = useContext(BreakpointTrackerContext);
    const isMobile = breakpoint === 'tablet' || breakpoint === 'phoneWide' || breakpoint === 'phone';
    const phoneNumber = CommonConstants.SAGA_PHONE_NUMBERS.SAGA_SMI_PHONE_NUMBER;
    const customerServicePhoneNumber = CommonConstants.SAGA_PHONE_NUMBERS.SAGA_SMI_CUSTOMER_SERVICES_NUMBER;

    const [openedAccordionId, setOpenedAccordionId] = useState('smiNonPurchasableAccordion0');

    const onAccordionStateChange = (openedAccordionsIds) => {
        setOpenedAccordionId(openedAccordionsIds[0]);
    };

    const returnToHomepageButtonContent = useMemo(() => {
        return (
            <div>
                <Link href={SAGA_HOME}>
                    <Button
                        type="button"
                        className="sgButton sgPrimaryButton"
                        id="returnToHomepageButton"
                    >
                        {translator(messages.SagaHomepage)}
                    </Button>
                </Link>
            </div>
        );
    }, [SAGA_HOME, translator]);

    const beginNewQuoteContent = useMemo(() => {
        return (
            <div>
                <Link href="#/journey" className="sgErrorPageNewQuoteLink">
                    <Button
                        type="button"
                        className="sgButton sgErrorPageNewQuoteLink"
                        id="returnToHomepageButton"
                    >
                        {translator(messages.NewQuote)}
                    </Button>
                </Link>
            </div>
        );
    }, [translator]);

    const renderGenericErrorWithSmiDetails = useMemo(() => {
        return (
            <>
                <p className="sgErrorPagePrimaryText">{translator(messages.SomethingGoneWrong)}</p>
                <p className="sgErrorPageSecondaryText">
                    {translator(messages.PleaseCallUs, {
                        phoneNumber: isMobile
                            ? <a href={`tel:${phoneNumber.replaceAll(' ', '')}`}>{phoneNumber}</a>
                            : <strong>{phoneNumber}</strong>
                    })}
                </p>
                <p className="sgErrorPageLinesOpen">{translator(messages.LinesOpen)}</p>
            </>
        );
    }, [isMobile, phoneNumber, translator]);

    const renderSmiPaymentMessagePurchaseError = useCallback(() => {
        return (
            <>
                <p className="sgErrorPagePrimaryText">{translator(messages.PurchaseNotComplete)}</p>
                <p className="sgErrorPageSecondaryText">
                    {translator(messages.PaymentCallUs, {
                        phoneNumber: isMobile
                            ? <a href={`tel:${phoneNumber.replaceAll(' ', '')}`}>{phoneNumber}</a>
                            : <strong>{phoneNumber}</strong>
                    })}
                </p>
                <p className="sgErrorPageLinesOpen">{translator(messages.LinesOpen)}</p>
            </>
        );
    }, [isMobile, phoneNumber, translator]);

    const renderUnableRetrieveTiaQuoteError = useCallback(() => {
        return (
            <>
                <p>
                    {htmlParser(translator(messages.RetrieveTiaQuoteMessage))}
                    <Link
                        target="_blank"
                        rel="noreferrer"
                        href="#/journey"
                        className="sgErrorPageNewQuoteLink"
                    >
                        {translator(messages.NewQuote2)}
                    </Link>
                </p>
            </>
        );
    }, [translator]);

    const getDeclineMessageContent = useCallback(() => {
        switch (errorCodeC) {
            case SG_SMI_ERROR_REASONS.CHANNEL_ISLANDS: // T0 C9128
                return (
                    <>
                        <p className="sgErrorPagePrimaryText">{translator(messages.ChannelIslandsError)}</p>
                        { returnToHomepageButtonContent }
                    </>
                );
            default:
                return (
                    <>
                        <p className="sgErrorPagePrimaryText">{translator(messages.DeclineErrorMessage)}</p>
                        { beginNewQuoteContent }
                        { returnToHomepageButtonContent }
                    </>
                );
        }
    }, [errorCodeC, beginNewQuoteContent, returnToHomepageButtonContent, translator, SG_SMI_ERROR_REASONS.CHANNEL_ISLANDS]);

    const getFraudulentCardErrorContent = useMemo(() => {
        return (
            <>
                <p className="sgErrorPagePrimaryText">
                    {translator(messages.SmiFraudulentCardMessageText1)}
                </p>
                <p className="sgErrorPageSecondaryText">
                    {translator(messages.SmiFraudulentCardMessageText2, {
                        phoneNumber: isMobile
                            ? <a href={`tel:${phoneNumber.replaceAll(' ', '')}`}>{phoneNumber}</a>
                            : <strong>{phoneNumber}</strong>
                    })}
                </p>
                <p className="sgErrorPageSecondaryText">
                    {translator(messages.SmiFraudulentCardMessageText3)}
                </p>
                <p className="sgErrorPageSecondaryText">
                    {translator(messages.LinesOpen)}
                </p>
            </>
        );
    }, [isMobile, phoneNumber, translator]);

    const getPhoneNumber = useMemo(() => { // for T9
        switch (openedAccordionId) {
            case 'smiNonPurchasableAccordion0':
            case 'smiNonPurchasableAccordion1':
                return customerServicePhoneNumber;
            case 'smiNonPurchasableAccordion2':
                return phoneNumber;
            default:
                return phoneNumber;
        }
    }, [customerServicePhoneNumber, openedAccordionId, phoneNumber]);

    const renderExistingCustomerErrorContent = useCallback(() => {
        return (
            <>
                <Accordion
                    closeOthers
                    disabled={false}
                    id="smiNonPurchasableAccordion"
                    showFrame={false}
                    onUpdateAccordionStates={onAccordionStateChange}
                    defaultOpenedId="smiNonPurchasableAccordion0"
                    className="sgErrorPageAccordion"
                >
                    <AccordionCard
                        id="smiNonPurchasableAccordion0"
                        isCollapsible
                        title={translator(messages.ExistingCustomerAnswer0)}
                        className="sgErrorPageAccordionCard"
                    >
                        <p>
                            {translator(messages.ExistingCustomerAnswer0Content1)}
                            <strong>
                                {translator(messages.ExistingCustomerAnswer0Content2, {
                                    phoneNumber: isMobile
                                        ? (
                                            <a href={`tel:${getPhoneNumber.replaceAll(' ', '')}`}>{customerServicePhoneNumber}</a>
                                        ) : getPhoneNumber
                                })}
                            </strong>
                        </p>
                        <p>{htmlParser(translator(messages.ExistingCustomerAnswer0Content3))}</p>
                    </AccordionCard>
                    <AccordionCard
                        id="smiNonPurchasableAccordion1"
                        isCollapsible
                        title={translator(messages.ExistingCustomerAnswer1)}
                        className="sgErrorPageAccordionCard"
                    >
                        <p>
                            {translator(messages.ExistingCustomerAnswer1Content1)}
                            <Link
                                target="_blank"
                                rel="noreferrer"
                                href={SmiConstants.POLICY_BOOKS_LINK}
                            >
                                {translator(messages.Here)}
                            </Link>
                            .
                        </p>
                        <p>
                            {translator(messages.ExistingCustomerAnswer1Content2)}
                            <strong>
                                {translator(messages.ExistingCustomerAnswer1Content3, {
                                    phoneNumber: isMobile
                                        ? (
                                            <a href={`tel:${getPhoneNumber.replaceAll(' ', '')}`}>{customerServicePhoneNumber}</a>
                                        ) : getPhoneNumber
                                })}
                            </strong>
                        </p>
                    </AccordionCard>
                    <AccordionCard
                        id="smiNonPurchasableAccordion2"
                        isCollapsible
                        title={translator(messages.ExistingCustomerAnswer2)}
                        className="sgErrorPageAccordionCard"
                    >
                        <p>
                            {translator(messages.ExistingCustomerAnswer2Content1)}
                        </p>
                        <p>
                            {translator(messages.ExistingCustomerAnswer2Content2)}
                            <strong>
                                {(translator(messages.ExistingCustomerAnswer2Content3, {
                                    phoneNumber: isMobile
                                        ? (
                                            <a href={`tel:${getPhoneNumber.replaceAll(' ', '')}`}>{phoneNumber}</a>
                                        ) : getPhoneNumber
                                }))}
                            </strong>
                            {translator(messages.ExistingCustomerAnswer2Content4)}
                        </p>

                    </AccordionCard>
                </Accordion>
                <div>
                    <Link href={SAGA_HOME}>
                        <Button>
                            {translator(messages.SagaHomepage)}
                        </Button>
                    </Link>
                </div>
            </>
        );
    }, [customerServicePhoneNumber, getPhoneNumber, isMobile, phoneNumber, translator, SAGA_HOME]);

    const renderRetrieveError = useMemo(() => {
        return (
            <>
                <p className="sgErrorPagePrimaryText">{translator(messages.WeAreSorryCannotRetrieveQuoteOnline)}</p>
                <p className="sgErrorPageSecondaryText">{translator(messages.PleaseGiveUsACallRetrieveQuoteOnline)}</p>
                <p className="sgErrorPageSecondaryText">{phoneNumber}</p>
                <p className="sgErrorPagePrimaryText">{translator(messages.OpeningHoursRetrieveQuoteOnline)}</p>
            </>
        );
    }, [phoneNumber, translator]);

    const headerContent = useMemo(() => {
        switch (errorCodeT) {
            case SG_ERROR_CODES.SAGA_DECLINE: // T0
                return translator(messages.SmiErrorHeader);
            case SG_ERROR_CODES.SAGA_REFERRAL: // T1
            case SG_ERROR_CODES.SAGA_QUOTATION_ERROR: // T6
                return translator(messages.ErrorHeader);
            case SG_ERROR_CODES.SAGA_NON_PURCHASABLE_QUOTE: // T9
                return translator(messages.ExistingCustomerHeader);
            case SG_ERROR_CODES.BARCLAYCARD_FRAUDULENT_CARD: // T8
            case SG_ERROR_CODES.SAGA_PAYMENT_FAILED: // T10
                return translator(messages.ErrorHeaderPayment);
            case SG_ERROR_CODES.SAGA_RETRIEVING_AMENDED_OFFLINE: // T13
                return translator(messages.ErrorHeaderRetrieve);
            case SG_ERROR_CODES.SAGA_RETRIEVE_TIA_QUOTE: // T17
                return translator(messages.RetrieveTiaQuoteHeader);
            default:
                return '';
        }
    }, [errorCodeT, SG_ERROR_CODES, translator]);

    const messageContent = useMemo(() => {
        switch (errorCodeT) {
            case SG_ERROR_CODES.SAGA_DECLINE: // T0
                return getDeclineMessageContent();
            case SG_ERROR_CODES.SAGA_QUOTATION_ERROR: // T6
                return renderGenericErrorWithSmiDetails;
            case SG_ERROR_CODES.BARCLAYCARD_FRAUDULENT_CARD: // T8
                return getFraudulentCardErrorContent;
            case SG_ERROR_CODES.SAGA_NON_PURCHASABLE_QUOTE: // T9
                return renderExistingCustomerErrorContent();
            case SG_ERROR_CODES.SAGA_PAYMENT_FAILED: // T10
                return renderSmiPaymentMessagePurchaseError();
            case SG_ERROR_CODES.SAGA_RETRIEVING_AMENDED_OFFLINE: // T13
                return renderRetrieveError;
            case SG_ERROR_CODES.SAGA_RETRIEVE_TIA_QUOTE: // T17
                return renderUnableRetrieveTiaQuoteError();
            default:
                return '';
        }
    }, [errorCodeT, SG_ERROR_CODES, renderRetrieveError, renderUnableRetrieveTiaQuoteError, getDeclineMessageContent, getFraudulentCardErrorContent,
        renderSmiPaymentMessagePurchaseError, renderGenericErrorWithSmiDetails, renderExistingCustomerErrorContent]);

    return (
        <div>
            <h1>{headerContent}</h1>
            <div>{messageContent}</div>
        </div>
    );
}

export default SgSmiError;
